import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MdAdd } from "react-icons/md";

export default function SelectedEvalAreaModal({
  setSelectedEvalAreaModal,
  initialData = {},
  onSave,
  handleToastCenterTop,
}) {
  const { t } = useTranslation();

  const [selectedEvalMethod, setSelectedEvalMethod] = useState("");
  const [evalItems, setEvalItems] = useState([]);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newItem, setNewItem] = useState({
    evalItem: "",
    detail: "",
    order: 1,
    active: true,
  });

  useEffect(() => {
    if (initialData.evalMethod) {
      setSelectedEvalMethod(initialData.evalMethod);
    }
    if (initialData.evalItems) {
      // 기존 데이터에 id가 없으면 생성
      const itemsWithId = initialData.evalItems.map((item, index) => ({
        ...item,
        id: item.id || `${Date.now()}${Math.floor(Math.random() * 100)}`,
      }));
      setEvalItems(itemsWithId);
    }
  }, [initialData]);

  const handleEvalMethodChange = (e) => {
    setSelectedEvalMethod(e.target.value);
    // 평가제가 바뀌어도 evalItems 초기화하지 않음
  };

  const handleNewItemChange = (e) => {
    const { name, value } = e.target;
    setNewItem((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddItem = () => {
    if (!newItem.evalItem.trim()) {
      handleToastCenterTop("평가항목 이름을 입력해주세요.");
      return;
    }

    // 중복 체크
    if (evalItems.some((item) => item.evalItem === newItem.evalItem.trim())) {
      handleToastCenterTop("같은 이름의 평가항목이 이미 존재합니다.");
      return;
    }

    const newEvalItem = {
      id: `${Date.now()}${Math.floor(Math.random() * 100)}`, // 간단한 고유 ID
      evalItem: newItem.evalItem.trim(),
      detail: newItem.detail,
      order: Number(newItem.order),
      active: true,
    };

    setEvalItems((prev) => {
      const updated = [...prev, newEvalItem].sort((a, b) => a.order - b.order);
      setNewItem({
        evalItem: "",
        detail: "",
        order: updated.length + 1,
        active: true,
      });
      return updated;
    });

    setIsAddingNew(false);
  };

  const handleEditItem = (id) => {
    setEditingId(id);
  };

  const handleSaveEdit = () => {
    if (editingId !== null) {
      const currentItem = evalItems.find((item) => item.id === editingId);
      const currentEvalItem = currentItem.evalItem.trim();

      // 빈 값 체크
      if (!currentEvalItem) {
        handleToastCenterTop("평가항목 이름을 입력해주세요.");
        return;
      }

      // 중복 체크 (자기 자신 제외)
      const isDuplicate = evalItems.some(
        (item) => item.id !== editingId && item.evalItem === currentEvalItem
      );

      if (isDuplicate) {
        handleToastCenterTop("같은 이름의 평가항목이 이미 존재합니다.");
        return;
      }
    }

    setEditingId(null);
  };

  const handleItemChange = (id, field, value) => {
    setEvalItems((prev) =>
      prev
        .map((item) =>
          item.id === id
            ? { ...item, [field]: field === "order" ? Number(value) : value }
            : item
        )
        .sort((a, b) => a.order - b.order)
    );
  };

  const handleDeleteItem = (id) => {
    if (window.confirm("이 항목을 삭제하시겠습니까?")) {
      setEvalItems((prev) => prev.filter((item) => item.id !== id));
      setEditingId(null); // 삭제 후 편집 모드 해제
    }
  };

  const handleToggleActive = (id) => {
    setEvalItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, active: !item.active } : item
      )
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedEvalMethod) {
      handleToastCenterTop("평가제를 선택해주세요.");
      return;
    }

    if (evalItems.length === 0) {
      handleToastCenterTop("최소 1개의 평가항목을 추가해주세요.");
      return;
    }

    const dataToSave = {
      evalMethod: selectedEvalMethod,
      evalItems: evalItems,
    };

    if (onSave) {
      onSave(dataToSave);
    }

    setSelectedEvalAreaModal(false);
  };

  const handleClose = () => {
    setSelectedEvalAreaModal(false);
  };

  return (
    <div className="modal-container">
      <div
        className="modalForUnit"
        style={{ width: "900px", maxHeight: "700px", overflowY: "auto" }}
      >
        <div className="modal-header mb-3 d-flex justify-content-between">
          <h3>지정평가영역 설정</h3>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
          >
            {t("common.labels.close") || "닫기"}
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            {/* 평가제 선택 */}
            <div className="row mb-3">
              <div className="col-3">
                <label className="col-form-label">
                  평가제 <span style={{ color: "red" }}>*</span>
                </label>
              </div>
              <div className="col-9">
                <select
                  className="form-control"
                  value={selectedEvalMethod}
                  onChange={handleEvalMethodChange}
                  required
                >
                  <option value="">평가제를 선택하세요</option>
                  <option value="이수제">이수제</option>
                  <option value="ABC">ABC</option>
                  <option value="직접입력">직접입력</option>
                  <option value="3단평가">3단평가</option>
                  <option value="4단평가">4단평가</option>
                  <option value="5단평가">5단평가</option>
                </select>
              </div>
            </div>

            {/* 평가항목 리스트 */}
            {selectedEvalMethod && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5>평가항목 ({selectedEvalMethod})</h5>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => setIsAddingNew(true)}
                    disabled={isAddingNew}
                  >
                    <MdAdd /> 항목 추가
                  </button>
                </div>

                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th style={{ width: "60px" }}>순서</th>
                      <th style={{ width: "200px" }}>평가항목</th>
                      <th>설명</th>
                      <th style={{ width: "80px" }}>상태</th>
                      <th style={{ width: "120px" }}>작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evalItems.map((item) => (
                      <tr
                        key={item.id}
                        className={!item.active ? "table-secondary" : ""}
                      >
                        <td>
                          {editingId === item.id ? (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={item.order}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "order",
                                  e.target.value
                                )
                              }
                              min="1"
                            />
                          ) : (
                            item.order
                          )}
                        </td>
                        <td>
                          {editingId === item.id ? (
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.evalItem}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "evalItem",
                                  e.target.value
                                )
                              }
                            />
                          ) : (
                            item.evalItem
                          )}
                        </td>
                        <td>
                          {editingId === item.id ? (
                            <textarea
                              className="form-control form-control-sm"
                              rows="2"
                              value={item.detail}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "detail",
                                  e.target.value
                                )
                              }
                            />
                          ) : (
                            item.detail
                          )}
                        </td>
                        <td className="text-center p-1">
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              item.active ? "btn-success" : "btn-secondary"
                            }`}
                            onClick={() => handleToggleActive(item.id)}
                          >
                            {item.active ? "활성" : "비활성"}
                          </button>
                        </td>
                        <td className="text-center p-1">
                          {editingId === item.id ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary me-1"
                                onClick={handleSaveEdit}
                              >
                                저장
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => setEditingId(null)}
                              >
                                취소
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => handleEditItem(item.id)}
                              >
                                수정
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteItem(item.id)}
                              >
                                삭제
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}

                    {/* 새 항목 추가 행 */}
                    {isAddingNew && (
                      <tr>
                        <td>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            name="order"
                            value={newItem.order}
                            onChange={handleNewItemChange}
                            min="1"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            name="evalItem"
                            value={newItem.evalItem}
                            onChange={handleNewItemChange}
                            placeholder="평가항목 이름"
                          />
                        </td>
                        <td>
                          <textarea
                            className="form-control form-control-sm"
                            name="detail"
                            rows="2"
                            value={newItem.detail}
                            onChange={handleNewItemChange}
                            placeholder="평가항목 설명"
                          />
                        </td>
                        <td className="text-center">
                          <span className="badge bg-success">활성</span>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary me-1"
                            onClick={handleAddItem}
                          >
                            추가
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => setIsAddingNew(false)}
                          >
                            취소
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={handleClose}
            >
              {t("common.labels.cancel") || "취소"}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!selectedEvalMethod || evalItems.length === 0}
            >
              {t("common.labels.save") || "저장"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
